<template>
    <div class="watchdog-tabs mb-0 px-5 mt-3">
        <button v-for="section in sections"
            :class="['watchdog-tab-btn', { active: selectedSection === section }]"
            @click="selectedSection = section"
        >
            {{ $t("screening_history.sections." + section) }}
        </button>
    </div>
    <div class="watchdog-wrapper-box p-5">
        <div class="mb-5 pt-1 d-flex align-items-center flex-wrap history-toolbar">
            <div class="d-flex align-items-center flex-wrap history-filters">
                <label class="mb-1 fw-bold px-1 pt-1">{{ $t("screening_history.search") }}:</label>
                <input class="form-control px-3 history_search_input" v-model="filters.query"
                    :placeholder="searchPlaceholder">

                <div class="type-pills">
                    <div v-if="hasTypes" class="facet">
                        <button :class="['type-pill', { active: filters.types.length || openFacet == 'type' }]"
                            @click.stop="toggleFacet('type')"
                        >
                            {{ $t("screening_history.facets.type") }}
                            <span v-if="filters.types.length" class="type-pill-count">
                                {{ filters.types.length }}
                            </span>
                            <span class="facet-caret">&#9662;</span>
                        </button>

                        <div v-if="openFacet == 'type'" class="facet-dropdown" @click.stop>
                            <label v-for="type in types" :key="type" class="facet-option">
                                <input type="checkbox"
                                    :checked="filters.types.includes(type)"
                                    @change="selectType(type)">
                                <span>{{ $t("screening_history.types." + type) }}</span>
                                <span v-if="selectedSection == 'watchdog'" class="type-pill-count">
                                    {{ formattedTypeCounts[type] }}
                                </span>
                            </label>
                        </div>
                    </div>

                    <div v-if="hasSources" class="facet">
                        <button :class="['type-pill', { active: filters.sources.length || openFacet == 'source' }]"
                            @click.stop="toggleFacet('source')"
                        >
                            {{ $t("screening_history.facets.source") }}
                            <span v-if="filters.sources.length" class="type-pill-count">
                                {{ filters.sources.length }}
                            </span>
                            <span class="facet-caret">&#9662;</span>
                        </button>

                        <div v-if="openFacet == 'source'" class="facet-dropdown" @click.stop>
                            <label v-for="source in sourceOptions" :key="source" class="facet-option">
                                <input type="checkbox"
                                    :checked="filters.sources.includes(source)"
                                    @change="selectSource(source)">
                                <span>{{ $t("screening_history.sources." + source) }}</span>
                            </label>
                        </div>
                    </div>

                    <div v-if="hasUsers" class="facet">
                        <button :class="['type-pill', { active: filters.users.length || openFacet == 'user' }]"
                            @click.stop="toggleFacet('user')"
                        >
                            {{ $t("screening_history.facets.user") }}
                            <span v-if="filters.users.length" class="type-pill-count">
                                {{ filters.users.length }}
                            </span>
                            <span class="facet-caret">&#9662;</span>
                        </button>

                        <div v-if="openFacet == 'user'" class="facet-dropdown" @click.stop>
                            <label v-for="user in userFacetOptions" :key="user.id" class="facet-option">
                                <input type="checkbox"
                                    :checked="filters.users.includes(user.id)"
                                    @change="selectUser(user.id)">
                                <span>
                                    {{ user.email }}
                                    <span v-if="user.self" class="fw-bold">({{ $t("screening_history.facets.self") }})</span>
                                </span>
                            </label>
                        </div>
                    </div>

                    <div v-if="hasStates" class="facet">
                        <button :class="['type-pill', { active: filters.states.length || openFacet == 'state' }]"
                            @click.stop="toggleFacet('state')"
                        >
                            {{ $t("screening_history.facets.state") }}
                            <span v-if="filters.states.length" class="type-pill-count">
                                {{ filters.states.length }}
                            </span>
                            <span class="facet-caret">&#9662;</span>
                        </button>

                        <div v-if="openFacet == 'state'" class="facet-dropdown" @click.stop>
                            <label v-for="state in stateOptions" :key="state" class="facet-option">
                                <input type="checkbox"
                                    :checked="filters.states.includes(state)"
                                    @change="selectState(state)">
                                <span>{{ $t("screening_history.states." + state) }}</span>
                            </label>
                        </div>
                    </div>

                    <div class="facet">
                        <button :class="['type-pill', { active: hasDateRange || openFacet == 'time' }]"
                            @click.stop="toggleFacet('time')"
                        >
                            {{ timeFacetLabel }}
                            <span v-if="hasDateRange" class="type-pill-count">1</span>
                            <span class="facet-caret">&#9662;</span>
                        </button>

                        <div v-if="openFacet == 'time'" class="facet-dropdown" @click.stop>
                            <label class="facet-option">
                                <span>{{ $t("screening_history.facets.dateFrom") }}</span>
                                <input type="date" class="form-control facet-date" v-model="filters.dateFrom">
                            </label>
                            <label class="facet-option">
                                <span>{{ $t("screening_history.facets.dateTo") }}</span>
                                <input type="date" class="form-control facet-date" v-model="filters.dateTo">
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <div class="ms-auto px-1 d-flex align-items-center">
                <label class="mb-1 fw-bold px-1 pt-1 text-nowrap">{{ $t("screening_history.sort") }}:</label>
                <select v-model="filters.sort" class="form-select ms-2 w-auto">
                    <option value="-created">{{ $t("screening_history.sortOptions.newest") }}</option>
                    <option value="created">{{ $t("screening_history.sortOptions.oldest") }}</option>
                    <option value="name">{{ $t("screening_history.sortOptions.nameAsc") }}</option>
                    <option value="-name">{{ $t("screening_history.sortOptions.nameDesc") }}</option>
                </select>
            </div>
        </div>
        <div class="w-100 table-responsive-lg px-1">
            <table class="w-100 mb-5 watchdog_table table">
                <!-- every section leads with the narrow icon column; the watchdog runs its
                     own two line layout inside a single spanning cell -->
                <colgroup>
                    <col style="width: 40px">
                    <col>
                    <col style="width: 30%">
                </colgroup>

                <tbody v-if="selectedSection == 'screenings'">
                    <tr
                        class="table_row align-items-center"
                        v-for="row in items"
                        :key="row.id"
                    >
                        <td class="align-middle">
                            <span v-if="row.kind == 'person'" class="person-icon">
                                <!-- bound, so vite leaves the runtime path alone -->
                                <img :src="'/static/v2/img/person.svg'" alt="">
                            </span>
                            <span v-else-if="row.kind == 'international'" class="fi fi-big fi-global"></span>
                            <span v-else class="fi fi-big" :class="'fi-' + row.kind"></span>
                        </td>

                        <td class="align-middle">
                            <a v-if="row.subject_url" :href="row.subject_url" target="_blank">
                                {{ row.name || '-' }}
                            </a>
                            <span v-else>{{ row.name || '-' }}</span>
                            <br/>

                            <span v-if="row.kind == 'person'">
                                <span v-if="row.birthdate">
                                    {{ $t("screening_history.birthdate") }}: {{ format_date(row.birthdate) }}
                                </span>
                                <span v-else>{{ $t("screening_history.birthdate") }}: ---</span>
                                <br/>
                                <span>{{ $t("screening_history.countries") }}: {{ row.countries.join(", ") || '---' }}</span>
                                <br/>
                            </span>
                            <span v-else-if="row.identifier">
                                <span v-if="row.kind == 'international'">BVD ID: {{ row.identifier }}</span>
                                <span v-else>IČO: {{ row.identifier }}</span>
                                <br/>
                            </span>

                            <span v-if="row.source" class="badge bg-secondary px-2 py-1 me-2 mt-1 rounded-pill">
                                {{ $t("screening_history.sources." + row.source) }}
                            </span>
                        </td>

                        <td class="align-middle user_cell">
                            <span v-if="row.screened_by && row.screened_by.email">
                                {{ row.screened_by.email }}<br/>
                            </span>
                            <i>{{ format_datetime(row.screened_at) }}</i>
                        </td>
                    </tr>
                </tbody>

                <tbody v-else-if="selectedSection == 'watchdog'">
                    <tr
                        v-for="row in items"
                        :key="row.kind + row.id"
                        class="table_row watchdog_card"
                        :class="{ watchdog_disabled: !row.enabled }"
                    >
                        <td colspan="3">
                            <div class="watchdog_card_body">
                                <div class="d-flex align-items-center gap-3">
                                    <span class="watchdog_meta">
                                        <span v-for="source in row.sources" class="badge bg-secondary px-2 py-1 me-1 rounded-pill">
                                            {{ $t("autoscreening.sources." + source) }}
                                        </span>
                                    </span>

                                    <div class="form-check form-switch d-flex align-items-center gap-2 m-0 ms-auto flex-shrink-0 watchdog_meta">
                                        <!-- the switch only asks, the modal decides - so it never toggles itself -->
                                        <input class="form-check-input m-0 cursor_pointer" type="checkbox" role="switch"
                                            :id="'watchdog-switch-' + row.kind + '-' + row.id"
                                            :checked="row.enabled" aria-haspopup="dialog"
                                            data-bs-toggle="modal" data-bs-target="#togglePopup"
                                            @click.prevent="toggleAutoscreening = row">
                                        <label class="form-check-label cursor_pointer"
                                            :for="'watchdog-switch-' + row.kind + '-' + row.id">
                                            {{ row.enabled ? $t("autoscreening.watching") : $t("autoscreening.disabledBadge") }}
                                        </label>
                                    </div>
                                </div>

                                <!-- what the subject is and how it stands -->
                                <div class="d-flex align-items-center gap-3">
                                    <span v-if="row.kind == 'person'" class="person-icon">
                                        <img :src="'/static/v2/img/person.svg'" alt="">
                                    </span>
                                    <span v-else-if="row.kind == 'international'" class="fi fi-big fi-global"></span>
                                    <span v-else class="fi fi-big" :class="'fi-' + row.kind"></span>

                                    <div class="flex-grow-1">
                                        <a :href="watchdogSubjectLink(row)" target="_blank">{{ row.name || '-' }}</a>
                                        <br/>

                                        <span v-if="row.kind == 'person'">
                                            <span v-if="row.birthdate">
                                                {{ $t("screening_history.birthdate") }}: {{ format_date(row.birthdate) }}
                                            </span>
                                            <span v-else>{{ $t("screening_history.birthdate") }}: ---</span>
                                            <span>, {{ $t("screening_history.countries") }}: {{ row.countries || '---' }}</span>
                                        </span>
                                        <span v-else-if="row.identifier">
                                            <span v-if="row.kind == 'international'">BVD ID: {{ row.identifier }}</span>
                                            <span v-else>IČO: {{ row.identifier }}</span>
                                        </span>
                                    </div>

                                    <div class="d-flex justify-content-start align-items-end flex-column">
                                        <!-- nosemgrep: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -->
                                        <div v-for="(flag, i) in row.flags" v-html="flag"
                                            class="autoscreening_flag_bubble"
                                            :class="{'mb-2': (i < row.flags.length - 1)}">
                                        </div>
                                    </div>
                                </div>

                                <hr class="watchdog_divider">

                                <!-- fixed lines, so every record has the same shape -->
                                <div class="d-flex align-items-center gap-4 watchdog_meta">
                                    <!-- only international autoscreenings expire, the rest are watched
                                         until somebody switches them off -->
                                    <span style="white-space: nowrap;">
                                        <span class="watchdog_meta_label">{{ $t("autoscreening.activeTo") }}&nbsp;</span>
                                        <span v-if="row.active_to">{{ format_date(row.active_to) }}</span>
                                        <span v-else>&infin;</span>
                                    </span>

                                    <span v-if="row.active_to" class="cursor_pointer" style="white-space: nowrap;"
                                        data-bs-toggle="modal" data-bs-target="#reactivationPopup"
                                        @click="reactivationAutoscreening = row">
                                        <span class="watchdog_meta_label">{{ $t("autoscreening.reactivate") }}&nbsp;</span>
                                        <span v-if="row.reactivate">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21"
                                                viewBox="0 0 24 24" fill="none" stroke="#4A9200"
                                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                                class="icon icon-tabler icons-tabler-outline icon-tabler-checkbox align-icon">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                <path d="M9 11l3 3l8 -8" />
                                                <path d="M20 12v6a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h9" />
                                            </svg>
                                        </span>
                                        <span v-else>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none" stroke="#000000"
                                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                                class="icon icon-tabler icons-tabler-outline icon-tabler-square align-icon">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                <path d="M3 3m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" />
                                            </svg>
                                        </span>
                                    </span>

                                    <!-- right aligned, above the switch. The separator carries its own
                                         spaces, vue's whitespace condensing eats the ones in the markup -->
                                    <span class="ms-auto text-end">
                                        <span v-if="row.created_by">{{ row.created_by }}</span>
                                        <span v-else>{{ $t("autoscreening.systemCreated") }}</span>
                                        <span class="watchdog_meta_label">&nbsp;&middot;&nbsp;</span>{{ format_datetime(row.created) }}
                                    </span>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>

                <tbody v-else>
                    <tr
                        class="table_row align-items-center"
                        v-for="(row, index) in items"
                        :key="index"
                    >
                        <td class="align-middle">
                            <svg v-if="creditKind(row) == 'blocked'" xmlns="http://www.w3.org/2000/svg"
                                width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e4002c"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                class="icon icon-tabler icon-tabler-lock">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-6z" />
                                <path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" />
                                <path d="M8 11v-4a4 4 0 1 1 8 0v4" />
                            </svg>

                            <svg v-else-if="creditKind(row) == 'given'" xmlns="http://www.w3.org/2000/svg"
                                width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4A9200"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                class="icon icon-tabler icon-tabler-gift">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M3 8m0 1a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1z" />
                                <path d="M12 8l0 13" />
                                <path d="M19 12v7a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-7" />
                                <path d="M7.5 8a2.5 2.5 0 0 1 0 -5a4.8 8 0 0 1 4.5 5a4.8 8 0 0 1 4.5 -5a2.5 2.5 0 0 1 0 5" />
                            </svg>

                            <svg v-else-if="creditKind(row) == 'new'" xmlns="http://www.w3.org/2000/svg"
                                width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4A9200"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                class="icon icon-tabler icon-tabler-circle-plus">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
                                <path d="M9 12l6 0" />
                                <path d="M12 9l0 6" />
                            </svg>

                            <svg v-else-if="creditKind(row) == 'autoscreening'" xmlns="http://www.w3.org/2000/svg"
                                width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#000000"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                class="icon icon-tabler icon-tabler-world">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
                                <path d="M3.6 9h16.8" />
                                <path d="M3.6 15h16.8" />
                                <path d="M11.5 3a17 17 0 0 0 0 18" />
                                <path d="M12.5 3a17 17 0 0 1 0 18" />
                            </svg>

                            <svg v-else xmlns="http://www.w3.org/2000/svg"
                                width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#000000"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                class="icon icon-tabler icon-tabler-circle-minus">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
                                <path d="M9 12l6 0" />
                            </svg>
                        </td>

                        <td class="align-middle">
                            <div v-if="creditKind(row) == 'blocked'">
                                <div v-if="row.bvd_id">
                                    <b>{{ $t('screening_history.blockedCredit') }}</b><br/>
                                    <div class="text-start w-100">
                                        <a :href="'/subject/opening/' + row.bvd_id">{{ $t('screening_history.linkToSubject') }}</a>
                                    </div>
                                </div>
                                <div v-else>
                                    <b>{{ $t('screening_history.blockedCreditWithoutLink') }}</b>
                                </div>
                            </div>

                            <div v-else-if="creditKind(row) == 'given'">
                                <b>{{ $t('screening_history.givenCredit') }}</b>
                            </div>

                            <div v-else-if="creditKind(row) == 'new'">
                                <b>
                                    {{ $t('screening_history.topUp') }}
                                    {{ ' ' + safeCount(row.count) + ' ' }}
                                    {{ $t('screening_history.globalVerification', safeCount(row.count)) }}
                                </b>
                            </div>

                            <div v-else-if="creditKind(row) == 'autoscreening'">
                                <b>{{ $t('screening_history.internationalAutoscreening') }}</b><br/>
                                <div class="text-start w-100">
                                    <a :href="'/subject/opening/' + row.autoscreening_bvd_id">{{ row.name }}</a>
                                </div>
                            </div>

                            <div v-else>
                                <b>{{ $t('screening_history.usedCredit') }}</b><br/>
                                <span v-if="row.identifier">IČO: {{ row.identifier }}<br/></span>
                                <span v-else-if="row.bvd_id">BVD ID: {{ row.bvd_id }}<br/></span>
                                <a v-if="row.subject_url" :href="row.subject_url" target="_blank">
                                    {{ row.name || '-' }}
                                </a>
                                <span v-else>{{ row.name || '-' }}</span>
                                <br/>
                            </div>
                        </td>

                        <td class="align-middle user_cell">
                            <span v-if="row.used_by && row.used_by.email">
                                {{ row.used_by.email }}<br/>
                            </span>
                            <i>{{ format_datetime(row.created_at) }}</i>
                            <span v-if="row.invoice">
                                <br/>
                                <a :href="'/payment/' + row.payment + '/invoice'" class="d-inline-flex align-items-center">
                                    {{ $t('screening_history.invoice') }}&nbsp;
                                    <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-download" width="16" height="16" viewBox="0 0 24 24" stroke-width="2" stroke="#e4002c" fill="none" stroke-linecap="round" stroke-linejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
                                        <path d="M7 11l5 5l5 -5" />
                                        <path d="M12 4l0 12" />
                                    </svg>
                                </a>
                            </span>
                            <span v-if="row.export_url">
                                <br/>
                                <a :href="row.export_url" class="d-inline-flex align-items-center">
                                    {{ $t('screening_history.generated_file') }}&nbsp;
                                    <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-download" width="16" height="16" viewBox="0 0 24 24" stroke-width="2" stroke="#e4002c" fill="none" stroke-linecap="round" stroke-linejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
                                        <path d="M7 11l5 5l5 -5" />
                                        <path d="M12 4l0 12" />
                                    </svg>
                                </a>
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-if="isLoading" class="text-center mb-5 pb-4">
            <div class="spinner-border text-primary" role="status" aria-label="Loading"></div>
        </div>
        <div v-if="!isLoading && !count" class="text-center mb-5 pb-4">
            <p>{{ $t(emptyKey) }}</p>
        </div>
        <div class="row paginator">
            <div class="col-3 px-3 d-flex align-items-center">
                <label class="mb-1 px-1 text-nowrap">{{ $t("screening_history.perPage") }}:</label>
                <select name="page_history" v-model="perPage" class="form-select ms-2 page_size_select" >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="1000">1000</option>
                </select>
            </div>
            <div class="col-6 d-flex justify-content-center">
                <ul v-if="maxPage > 1" class="pagination flex-wrap mb-0">
                    <li class="page-item" v-if="page > 1">
                        <a class="page-link" href="#" @click.prevent="setPage(1)">
                            &laquo; {{ $t("screening_history.pagination.first") }}
                        </a>
                    </li>
                    <li class="page-item" v-if="page > 1">
                        <a class="page-link" href="#" @click.prevent="setPage(page - 1)">
                            &larr; {{ $t("screening_history.pagination.previous") }}
                        </a>
                    </li>

                    <li class="page-item" v-for="num in pageNumbers" :key="num" :class="{ active: num == page }">
                        <a class="page-link" href="#" @click.prevent="setPage(num)">{{ num }}</a>
                    </li>

                    <li class="page-item" v-if="page < maxPage">
                        <a class="page-link" href="#" @click.prevent="setPage(page + 1)">
                            {{ $t("screening_history.pagination.next") }} &rarr;
                        </a>
                    </li>
                    <li class="page-item" v-if="page < maxPage">
                        <a class="page-link" href="#" @click.prevent="setPage(maxPage)">
                            {{ $t("screening_history.pagination.last") }} &raquo;
                        </a>
                    </li>
                </ul>
            </div>
            <div class="col-3 text-end mt-2 pe-3">
                <span>{{ $t("screening_history.page", {page: page, maxPage: maxPage}) }}</span>
            </div>
        </div>
    </div>

    <!-- Modal -->
    <div class="modal fade" id="reactivationPopup" tabindex="-1" aria-labelledby="reactivationPopupLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
        <div class="modal-header">
            <h5 class="modal-title" id="reactivationPopupLabel">{{ $t("autoscreening.prolongationPopup") }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body text-start">
            <p class="mb-0" v-if="reactivationAutoscreening.reactivate">{{ $t("autoscreening.paragraphStopReactivating") }}</p>
            <p class="mb-0" v-else>{{ $t("autoscreening.paragraphReactivating") }}</p>
        </div>
        <div class="modal-footer">
            <button v-if="reactivationAutoscreening.reactivate" class="btn btn-primary" data-bs-dismiss="modal" @click="stopReactivation(reactivationAutoscreening.id)">{{ $t("autoscreening.dontReactivate") }}</button>
            <button v-else class="btn btn-primary" data-bs-dismiss="modal" @click="reactivation(reactivationAutoscreening.id)">{{ $t("autoscreening.reactivateAgain") }}</button>
        </div>

        </div>
    </div>
    </div>

    <div class="modal fade" id="togglePopup" tabindex="-1" aria-labelledby="togglePopupLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
        <div class="modal-header">
            <h5 class="modal-title" id="togglePopupLabel">{{ $t("autoscreening.togglePopup") }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body text-start">
            <p class="mb-0" v-if="toggleAutoscreening.enabled">{{ $t("autoscreening.paragraphDisabling") }}</p>
            <p class="mb-0" v-else>{{ $t("autoscreening.paragraphEnabling") }}</p>
        </div>
        <div class="modal-footer">
            <button v-if="toggleAutoscreening.enabled" class="btn btn-primary" data-bs-dismiss="modal" @click="toggleWatchdog(toggleAutoscreening.id, false)">{{ $t("autoscreening.disableWatchdog") }}</button>
            <button v-else class="btn btn-primary" data-bs-dismiss="modal" @click="toggleWatchdog(toggleAutoscreening.id, true)">{{ $t("autoscreening.enableWatchdog") }}</button>
        </div>

        </div>
    </div>
    </div>
</template>

<script>
import axios from "axios";
import moment from 'moment'

const api = axios.create({
    baseURL: "",
    withCredentials: true,
    xsrfCookieName: "csrftoken",
    xsrfHeaderName: "X-CSRFToken"
});

// the log records how a screening was requested, an autoscreening records what
// created it - two different vocabularies behind the same pill
const SCREENING_SOURCES = ['web', 'mass', 'api'];
const WATCHDOG_SOURCES = ['web', 'api', 'czech_contract_registry', 'import'];
const WATCHDOG_STATES = ['enabled', 'disabled'];

// filter name -> api parameter, every new facet adds one entry here
const PARAM_NAMES = {
    query: "q",
    types: "selected_type",
    sources: "source",
    users: "user",
    states: "state",
    dateFrom: "date_from",
    dateTo: "date_to",
    sort: "sort"
};

export default {
    name: "ScreeningHistory",
    data() {
        return {
            items: [],
            // everything the user can narrow the table by - page and perPage stay
            // out, they are a position in the result, not a criterion
            filters: {
                query: "",
                types: [],
                sources: [],
                users: [],
                states: [],
                dateFrom: "",
                dateTo: "",
                sort: "-created"
            },
            page: 1,
            perPage: 20,
            count: 0,
            isLoading: false,
            cancelToken: null,
            searchTimer: null,
            debounceTime: 700,
            actualString: null,
            reactivationAutoscreening: {
                reactivate: null,
                id: null
            },
            toggleAutoscreening: {
                enabled: null,
                id: null
            },
            sections: ['screenings', 'credits'],
            selectedSection: 'screenings',
            typesCounts: {},
            screeningTypes: ['cz', 'sk', 'international', 'person'],
            watchdogTypes: ['cz', 'sk', 'international', 'person'],
            userOptions: [],
            openFacet: null,
            // set on the admin site, where the table answers for another institution
            institutionId: null
        };
    },
    mounted: function () {
        if (document.getElementById("language")) {
            this.$i18n.locale = document.getElementById("language").innerText;
        }

        const institution = document.getElementById("institution");
        if (institution) {
            this.institutionId = institution.innerText.trim();
        }

        this.screeningTypes = this.readList("screening_types", this.screeningTypes);
        this.watchdogTypes = this.readList("watchdog_types", this.watchdogTypes);
        this.userOptions = this.readJson("facet_users");

        const sections = this.readList("profile_sections", this.sections);
        if (sections.length) {
            this.sections = sections;
            this.selectedSection = sections[0];
        }

        this.filters = this.defaultFilters();
        this.searchHistory();

        document.addEventListener("click", this.closeFacets);
    },
    unmounted: function () {
        document.removeEventListener("click", this.closeFacets);
    },
    computed: {
        types: function () {
            return this.selectedSection == 'watchdog' ? this.watchdogTypes : this.screeningTypes;
        },
        hasTypes: function () {
            return this.selectedSection != 'credits';
        },
        hasSources: function () {
            // credit movements have no channel to narrow by
            return this.selectedSection != 'credits';
        },
        sourceOptions: function () {
            return this.selectedSection == 'watchdog' ? WATCHDOG_SOURCES : SCREENING_SOURCES;
        },
        hasUsers: function () {
            // one account is nothing to pick from, but a watchdog can also have no author
            // at all, so there the single account still makes two buckets
            return this.selectedSection == 'watchdog'
                ? this.userOptions.length > 0
                : this.userOptions.length > 1;
        },
        userFacetOptions: function () {
            if (this.selectedSection != 'watchdog') {
                return this.userOptions;
            }

            return [...this.userOptions,
                    { id: 0, email: this.$t("autoscreening.systemCreated"), self: false }];
        },
        hasStates: function () {
            // only a watchdog can be switched off
            return this.selectedSection == 'watchdog';
        },
        stateOptions: function () {
            return WATCHDOG_STATES;
        },
        timeFacetLabel: function () {
            // the watchdog narrows by when a subject was added
            return this.selectedSection == 'watchdog'
                ? this.$t("screening_history.facets.timeAdded")
                : this.$t("screening_history.facets.time");
        },
        hasDateRange: function () {
            return Boolean(this.filters.dateFrom || this.filters.dateTo);
        },
        historyUrl: function () {
            return this.institutionId
                ? `/adminsite/api/institution/${this.institutionId}/history`
                : "/api/profile/history";
        },
        reactivateUrls: function () {
            if (this.institutionId) {
                const base = `/adminsite/api/autoscreenings/${this.institutionId}`;
                return { on: `${base}/reactivate`, off: `${base}/stop/reactivate` };
            }
            return { on: "/api/autoscreening/reactivate", off: "/api/autoscreening/stop/reactivate" };
        },
        toggleUrl: function () {
            return this.institutionId
                ? `/adminsite/api/autoscreenings/${this.institutionId}/toggle`
                : "/api/autoscreening/toggle";
        },
        searchPlaceholder: function () {
            if (this.filters.types.length == 1) {
                return this.$t("screening_history.filter.placeholder." + this.filters.types[0]);
            }
            return this.$t("screening_history.placeholder");
        },
        emptyKey: function () {
            if (this.selectedSection == 'watchdog') {
                return "autoscreening.empty";
            }
            if (this.selectedSection == 'credits') {
                return "screening_history.emptyCredits";
            }
            return "screening_history.empty";
        },
        formattedTypeCounts: function () {
            const out = {};
            for (const type of this.types) {
                out[type] = this.formatCountTis(this.typesCounts?.[type] ?? 0);
            }
            return out;
        },
        maxPage: function () {
            return Math.ceil(this.count/this.perPage)
        },
        pageNumbers: function () {
            const numbers = [];
            for (let num = Math.max(1, this.page - 2); num <= Math.min(this.maxPage, this.page + 2); num++) {
                numbers.push(num);
            }
            return numbers;
        }
    },
    watch: {
        filters: {
            handler() {
                this.page = 1;
                this.searchHistory();
            },
            deep: true
        },
        page: function () {
            this.searchHistory();
        },
        perPage: function () {
            this.page = 1;
            this.searchHistory();
        },
        selectedSection: function () {
            this.page = 1;
            this.filters = this.defaultFilters();
            this.searchHistory();
        },
    },
    methods: {
        toggleFacet(facet) {
            this.openFacet = this.openFacet == facet ? null : facet;
        },
        closeFacets() {
            this.openFacet = null;
        },
        defaultFilters() {
            // every section starts unfiltered, newest first
            return {
                query: "",
                types: [],
                sources: [],
                users: [],
                states: [],
                dateFrom: "",
                dateTo: "",
                sort: "-created"
            };
        },
        selectType(type) {
            if (this.filters.types.includes(type)) {
                this.filters.types = this.filters.types.filter(selected => selected != type);
            } else {
                this.filters.types = [...this.filters.types, type];
            }
        },
        selectSource(source) {
            if (this.filters.sources.includes(source)) {
                this.filters.sources = this.filters.sources.filter(selected => selected != source);
            } else {
                this.filters.sources = [...this.filters.sources, source];
            }
        },
        selectUser(userId) {
            if (this.filters.users.includes(userId)) {
                this.filters.users = this.filters.users.filter(selected => selected != userId);
            } else {
                this.filters.users = [...this.filters.users, userId];
            }
        },
        selectState(state) {
            if (this.filters.states.includes(state)) {
                this.filters.states = this.filters.states.filter(selected => selected != state);
            } else {
                this.filters.states = [...this.filters.states, state];
            }
        },
        readJson(elementId) {
            const el = document.getElementById(elementId);
            return el ? JSON.parse(el.textContent) : [];
        },
        readList(elementId, fallback) {
            const el = document.getElementById(elementId);
            if (!el) {
                return fallback;
            }
            const value = el.innerText.trim();
            return value ? value.split(",") : [];
        },
        format_date(value) {
            if (value) {
                return moment(String(value)).format('DD.MM.YYYY')
            }
            return "-"
        },
        format_datetime(value) {
            if (value) {
                return moment(String(value)).format('DD.MM.YYYY HH:mm')
            }
        },
        formatCountTis(value) {
            const n = Number(value) || 0;

            if (n < 1000) {
                return n.toString();
            }

            if (n < 1_000_000) {
                const thousands = Math.floor(n / 1000);
                return `${thousands} tis.`;
            }

            return new Intl.NumberFormat("cs-CZ", {
                notation: "compact",
                compactDisplay: "short",
                maximumFractionDigits: 1
            }).format(n);
        },
        creditKind(row) {
            // the ledger flags a movement rather than naming it - the order
            // matters, a blocked credit carries a bvd id like a spent one does
            if (row.blocked) {
                return 'blocked';
            }
            if (row.given_credit) {
                return 'given';
            }
            if (row.new) {
                return 'new';
            }
            if (row.autoscreening_bvd_id) {
                return 'autoscreening';
            }
            return 'used';
        },
        safeCount(value) {
            if (value === null || value === undefined) {
                return 0;
            }
            const n = Number(value);
            return Number.isFinite(n) ? n : 0;
        },
        setPage: function (page) {
            this.page = page;
        },
        watchdogSubjectLink(row) {
            if (row.kind == 'person') {
                return '/person/' + row.hash;
            }
            if (row.kind == 'international') {
                return '/subject/opening/' + row.identifier;
            }
            return '/subject/' + row.source_country + '/id/' + row.url_identifier;
        },
        stopReactivation: function (autoscreening_id) {
            api.get(this.reactivateUrls.off, {
                params: { selected_type: this.reactivationAutoscreening.kind, autoscreening_id: autoscreening_id }
            }).finally(() => {
                this.searchHistory();
            })
        },
        reactivation: function (autoscreening_id) {
            api.get(this.reactivateUrls.on, {
                params: { selected_type: this.reactivationAutoscreening.kind, autoscreening_id: autoscreening_id }
            }).finally(() => {
                this.searchHistory();
            })
        },
        toggleWatchdog: function (autoscreening_id, enabled) {
            api.get(this.toggleUrl, {
                params: {
                    selected_type: this.toggleAutoscreening.kind,
                    autoscreening_id: autoscreening_id,
                    enabled: enabled
                }
            }).finally(() => {
                this.searchHistory();
            })
        },
        requestParams() {
            const params = {
                section: this.selectedSection,
                per_page: this.perPage,
                page: this.page,
            };

            for (const [name, value] of Object.entries(this.filters)) {
                const serialized = Array.isArray(value) ? value.join(",") : value;
                if (serialized !== "" && serialized !== null && serialized !== undefined) {
                    params[PARAM_NAMES[name]] = serialized;
                }
            }

            return params;
        },
        searchHistory: function () {
            var query = this.filters.query;
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }

            if (this.cancelToken != null) {
                this.cancelToken.cancel("Operation canceled by the user.");
                this.cancelToken = null;
            }

            this.isLoading = true;
            this.items = [];
            this.actualString = query;

            let params = this.requestParams();

            this.searchTimer = setTimeout(function () {
            this.cancelToken = axios.CancelToken.source();
            api.get(this.historyUrl, { params: params, cancelToken: this.cancelToken.token })
                .then(function (r) {
                    if (query == this.actualString) {
                        this.items = r.data.items;
                        this.count = r.data.count;
                        this.typesCounts = r.data.types_counts ?? this.typesCounts;
                    }
                }.bind(this))
                .catch(e => {
                    if (axios.isCancel(e)) {
                        console.log("Request canceled", e.message);
                    } else {
                        console.log(e);
                    }
                })
                .finally(() => {
                    this.cancelToken = null;
                    if (query == this.actualString) {
                        this.isLoading = false;
                    }
                })
            }.bind(this), this.debounceTime);
        },
    },
}
</script>
